import type { LucideIcon } from 'lucide-react'
import {
  Archive,
  ArrowRightCircle,
  BadgeCheck,
  Ban,
  Banknote,
  Briefcase,
  Building2,
  CheckCircle,
  CloudAlert,
  Coins,
  Frown,
  Gift,
  Globe,
  HandCoins,
  Info,
  Loader2,
  OctagonAlert,
  PartyPopper,
  PauseCircle,
  PlayCircle,
  Receipt,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  Trophy,
  UserCheck,
  UserPlus,
  UserX,
  XCircle,
} from 'lucide-react'
import type { AppNotificationRecord } from '@/types/notifications'

export interface NotificationSemantic {
  icon: LucideIcon
  iconClass: string
  mediaClass: string
  hoverClass: string
  badgeClass: string
  badgeLabelKey: string
}

/* ── Explicit static class maps so Tailwind discovers every dark: variant ── */

const styles = {
  sky: {
    iconClass: 'text-sky-600 dark:text-sky-400',
    mediaClass:
      'bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/20 text-sky-600 dark:text-sky-400',
    hoverClass: 'hover:bg-sky-50 dark:hover:bg-sky-500/10',
    badgeClass:
      'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400 hover:bg-sky-100/80 dark:hover:bg-sky-500/15 border-sky-200 dark:border-sky-500/20',
  },
  rose: {
    iconClass: 'text-rose-600 dark:text-rose-400',
    mediaClass:
      'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400',
    hoverClass: 'hover:bg-rose-50 dark:hover:bg-rose-500/10',
    badgeClass:
      'bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400 hover:bg-rose-100/80 dark:hover:bg-rose-500/15 border-rose-200 dark:border-rose-500/20',
  },
  amber: {
    iconClass: 'text-amber-600 dark:text-amber-400',
    mediaClass:
      'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20 text-amber-600 dark:text-amber-400',
    hoverClass: 'hover:bg-amber-50 dark:hover:bg-amber-500/10',
    badgeClass:
      'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-100/80 dark:hover:bg-amber-500/15 border-amber-200 dark:border-amber-500/20',
  },
  emerald: {
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    mediaClass:
      'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
    hoverClass: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10',
    badgeClass:
      'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100/80 dark:hover:bg-emerald-500/15 border-emerald-200 dark:border-emerald-500/20',
  },
  violet: {
    iconClass: 'text-violet-600 dark:text-violet-400',
    mediaClass:
      'bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/20 text-violet-600 dark:text-violet-400',
    hoverClass: 'hover:bg-violet-50 dark:hover:bg-violet-500/10',
    badgeClass:
      'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-400 hover:bg-violet-100/80 dark:hover:bg-violet-500/15 border-violet-200 dark:border-violet-500/20',
  },
  blue: {
    iconClass: 'text-blue-600 dark:text-blue-400',
    mediaClass:
      'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 text-blue-600 dark:text-blue-400',
    hoverClass: 'hover:bg-blue-50 dark:hover:bg-blue-500/10',
    badgeClass:
      'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 hover:bg-blue-100/80 dark:hover:bg-blue-500/15 border-blue-200 dark:border-blue-500/20',
  },
  orange: {
    iconClass: 'text-orange-600 dark:text-orange-400',
    mediaClass:
      'bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-400',
    hoverClass: 'hover:bg-orange-50 dark:hover:bg-orange-500/10',
    badgeClass:
      'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400 hover:bg-orange-100/80 dark:hover:bg-orange-500/15 border-orange-200 dark:border-orange-500/20',
  },
  zinc: {
    iconClass: 'text-zinc-600 dark:text-zinc-400',
    mediaClass:
      'bg-zinc-50 dark:bg-zinc-500/10 border-zinc-200 dark:border-zinc-500/20 text-zinc-600 dark:text-zinc-400',
    hoverClass: 'hover:bg-zinc-50 dark:hover:bg-zinc-500/10',
    badgeClass:
      'bg-zinc-100 dark:bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 hover:bg-zinc-100/80 dark:hover:bg-zinc-500/15 border-zinc-200 dark:border-zinc-500/20',
  },
  cyan: {
    iconClass: 'text-cyan-600 dark:text-cyan-400',
    mediaClass:
      'bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-600 dark:text-cyan-400',
    hoverClass: 'hover:bg-cyan-50 dark:hover:bg-cyan-500/10',
    badgeClass:
      'bg-cyan-100 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100/80 dark:hover:bg-cyan-500/15 border-cyan-200 dark:border-cyan-500/20',
  },
  teal: {
    iconClass: 'text-teal-600 dark:text-teal-400',
    mediaClass:
      'bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/20 text-teal-600 dark:text-teal-400',
    hoverClass: 'hover:bg-teal-50 dark:hover:bg-teal-500/10',
    badgeClass:
      'bg-teal-100 dark:bg-teal-500/15 text-teal-700 dark:text-teal-400 hover:bg-teal-100/80 dark:hover:bg-teal-500/15 border-teal-200 dark:border-teal-500/20',
  },
} as const

function c(color: keyof typeof styles) {
  return styles[color]
}

const severityFallbacks: Record<string, NotificationSemantic> = {
  danger:  { icon: XCircle,      badgeLabelKey: 'notifications.severity.alert',    ...c('rose') },
  warning: { icon: ShieldAlert,  badgeLabelKey: 'notifications.severity.warning',  ...c('amber') },
  success: { icon: CheckCircle,  badgeLabelKey: 'notifications.severity.success',  ...c('emerald') },
  info:    { icon: Info,         badgeLabelKey: 'notifications.severity.info',     ...c('sky') },
}

const eventSemanticMap: Record<string, NotificationSemantic> = {
  agent_invited:           { icon: UserPlus,         badgeLabelKey: 'notifications.types.agent',        ...c('violet') },
  agent_activation:        { icon: UserCheck,        badgeLabelKey: 'notifications.types.agent',        ...c('emerald') },
  agent_suspended:         { icon: UserX,            badgeLabelKey: 'notifications.types.agent',        ...c('amber') },
  agent_reactivated:       { icon: UserCheck,        badgeLabelKey: 'notifications.types.agent',        ...c('emerald') },

  program_assigned:        { icon: Briefcase,        badgeLabelKey: 'notifications.types.program',      ...c('blue') },
  program_unassigned:      { icon: Briefcase,        badgeLabelKey: 'notifications.types.program',      ...c('orange') },
  program_activated:       { icon: Rocket,           badgeLabelKey: 'notifications.types.program',      ...c('emerald') },
  program_paused:          { icon: PauseCircle,      badgeLabelKey: 'notifications.types.program',      ...c('amber') },
  program_resumed:         { icon: PlayCircle,       badgeLabelKey: 'notifications.types.program',      ...c('blue') },
  program_suspended:       { icon: ShieldAlert,      badgeLabelKey: 'notifications.types.program',      ...c('amber') },
  program_rewards_updated: { icon: Gift,             badgeLabelKey: 'notifications.types.program',      ...c('violet') },
  program_archived:        { icon: Archive,          badgeLabelKey: 'notifications.types.program',      ...c('zinc') },

  business_invited:        { icon: Building2,        badgeLabelKey: 'notifications.types.business',     ...c('zinc') },
  business_approved:       { icon: BadgeCheck,       badgeLabelKey: 'notifications.types.business',     ...c('emerald') },
  business_rejected:       { icon: XCircle,          badgeLabelKey: 'notifications.types.business',     ...c('rose') },

  prospect_submitted:        { icon: UserPlus,         badgeLabelKey: 'notifications.types.prospect', ...c('sky') },
  prospect_submitted_public: { icon: Globe,            badgeLabelKey: 'notifications.types.prospect', ...c('cyan') },
  prospect_status_changed:   { icon: ArrowRightCircle, badgeLabelKey: 'notifications.types.prospect', ...c('cyan') },
  prospect_converted:        { icon: Trophy,           badgeLabelKey: 'notifications.types.prospect', ...c('emerald') },
  prospect_lost:             { icon: Frown,            badgeLabelKey: 'notifications.types.prospect', ...c('orange') },

  transaction_detected:  { icon: Receipt,     badgeLabelKey: 'notifications.types.transaction', ...c('teal') },
  transaction_validated: { icon: ShieldCheck, badgeLabelKey: 'notifications.types.transaction', ...c('emerald') },
  transaction_paid:      { icon: Banknote,    badgeLabelKey: 'notifications.types.transaction', ...c('teal') },

  points_earned: { icon: Coins, badgeLabelKey: 'notifications.types.points', ...c('amber') },

  exchange_requested:  { icon: HandCoins,   badgeLabelKey: 'notifications.types.exchange', ...c('blue') },
  exchange_approved:   { icon: CheckCircle, badgeLabelKey: 'notifications.types.exchange', ...c('emerald') },
  exchange_rejected:   { icon: XCircle,     badgeLabelKey: 'notifications.types.exchange', ...c('rose') },
  exchange_processing: { icon: Loader2,     badgeLabelKey: 'notifications.types.exchange', ...c('violet') },
  exchange_completed:  { icon: PartyPopper, badgeLabelKey: 'notifications.types.exchange', ...c('emerald') },
  exchange_cancelled:  { icon: Ban,         badgeLabelKey: 'notifications.types.exchange', ...c('amber') },

  sync_job_failed:        { icon: CloudAlert,   badgeLabelKey: 'notifications.types.sync', ...c('rose') },
  sync_job_dead_lettered: { icon: OctagonAlert, badgeLabelKey: 'notifications.types.sync', ...c('rose') },
}

const typeBadgeLabelKeys: Record<string, string> = {
  agent: 'notifications.types.agent',
  program: 'notifications.types.program',
  business: 'notifications.types.business',
  platform: 'notifications.types.platform',
  prospect: 'notifications.types.prospect',
  exchange: 'notifications.types.exchange',
  transaction: 'notifications.types.transaction',
  sync: 'notifications.types.sync',
  points: 'notifications.types.points',
}

export function getNotificationSemantic(item: AppNotificationRecord): NotificationSemantic {
  const event = (item.metadata?.event as string) ?? ''

  if (event && eventSemanticMap[event]) {
    const mapped = eventSemanticMap[event]
    return {
      ...mapped,
      badgeLabelKey: typeBadgeLabelKeys[item.notification_type] ?? mapped.badgeLabelKey,
    }
  }

  const severity = item.severity ?? 'info'
  const fallback = severityFallbacks[severity] ?? severityFallbacks.info

  return {
    ...fallback,
    badgeLabelKey: typeBadgeLabelKeys[item.notification_type] ?? fallback.badgeLabelKey,
  }
}
